/**
 * Переходы режима «Основы».
 *
 * Режим собирает занятие сам, но mastery и XP считаются теми же функциями,
 * что и после обычной тренировки: результат для пользователя один и тот же,
 * а два разных пути его подсчёта разошлись бы (docs/ARCHITECTURE.md §5).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import { DEFAULT_LEARNING_CONFIG } from '@type2x/learning-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import {
  activeProfile,
  appendXp,
  recordSession,
  upsertMastery,
} from '@type2x/feature-personal-workspace';
import {
  applyKeyOutcomes,
  buildFundamentalsPlan,
  collectKeyOutcomes,
  hasFundamentals,
  type FundamentalsPlan,
  type FundamentalsSnapshot,
} from '@type2x/feature-fundamentals';
import { buildSessionSummary } from '@type2x/feature-progress';
import { buildSessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from './app-state.js';
import { countXpToday, practiceScreen, resultScreen, type Transition } from './flow.js';

const CONFIG = DEFAULT_LEARNING_CONFIG;

/** Снимок навыка для режима «Основы». */
export function fundamentalsSnapshot(workspace: Workspace): FundamentalsSnapshot {
  const profile = activeProfile(workspace);

  return {
    language: profile.language,
    layoutId: profile.keyboard.layoutId,
    units: profile.units,
    stats: profile.stats,
  };
}

/** Остались ли режиму непройденные группы клавиш. */
export function fundamentalsAvailable(workspace: Workspace | null): boolean {
  return workspace !== null && hasFundamentals(fundamentalsSnapshot(workspace));
}

/** Экран режима со свежим занятием. */
export function fundamentalsScreen(runtime: Runtime, workspace: Workspace): AppScreen {
  return {
    name: 'fundamentals',
    plan: planFor(runtime, workspace),
    sessionId: runtime.ids.newSessionId(),
  };
}

/**
 * Ведёт ли основная колея в «Основы».
 *
 * Начавший с основ возвращается к основам, пока есть непройденные группы:
 * бросать новичка в персональную тренировку по клавишам, которых он ещё не
 * знает, — это тот же обескураживающий первый экран, ради ухода от которого
 * режим и создан.
 *
 * Вынесено из `resumeScreen`, потому что тот же ответ нужен экрану результата:
 * кнопка обязана называть, куда ведёт, а не просто вести.
 */
export function resumesToFundamentals(workspace: Workspace): boolean {
  return activeProfile(workspace).startPath === 'fundamentals' && fundamentalsAvailable(workspace);
}

/** Экран, с которого продолжает вернувшийся пользователь. */
export function resumeScreen(runtime: Runtime, workspace: Workspace): AppScreen {
  return resumesToFundamentals(workspace)
    ? fundamentalsScreen(runtime, workspace)
    : practiceScreen(runtime, workspace);
}

/** Строит занятие по текущему состоянию навыка. */
export function planFor(runtime: Runtime, workspace: Workspace): FundamentalsPlan {
  return buildFundamentalsPlan(
    fundamentalsSnapshot(workspace),
    `${runtime.installationId}:${runtime.clock.epochMs()}`,
  );
}

/** Завершает занятие: обновляет клавиши, начисляет XP и собирает итог. */
export function finishFundamentals(
  runtime: Runtime,
  workspace: Workspace,
  plan: FundamentalsPlan,
  session: TypingSession,
  sessionId: SessionId,
): Transition {
  const now = runtime.clock.isoNow();
  const profile = activeProfile(workspace);

  const outcomes = collectKeyOutcomes(plan.exercise, session.keystrokeEvents, session.typingErrors);
  const statsAfter = applyKeyOutcomes(plan.units, profile.stats, outcomes, CONFIG, now);

  // Клавиша могла встретиться впервые: её единица создаётся вместе с итогом.
  let next = upsertMastery(workspace, plan.units, statsAfter, now);
  next = recordSession(next, session.metrics, now);

  const summary = buildSessionSummary({
    metrics: session.metrics,
    baseline: profile.baseline,
    units: activeProfile(next).units,
    statsBefore: profile.stats,
    statsAfter,
    previousXp: next.xpLedger.reduce((sum, item) => sum + item.amount, 0),
    xpOccurrencesToday: countXpToday(next, now),
    activeDays: activeProfile(next).practiceDays.length,
    retained: false,
    isControlled: false,
    awardedAt: now,
    config: CONFIG,
  });

  for (const transaction of summary.xpAwarded) {
    next = appendXp(next, transaction, now);
  }

  return {
    workspace: next,
    screen: resultScreen(next, summary, { kind: 'fundamentals' }, now),
    sessionPayload: buildSessionPayload({
      syncId: runtime.ids.newSyncId(),
      sessionId,
      mode: 'fundamentals',
      language: profile.language,
      layoutId: profile.keyboard.layoutId,
      startedAt: new Date(runtime.clock.epochMs() - session.metrics.elapsedMs).toISOString(),
      durationMs: session.metrics.elapsedMs,
      metrics: session.metrics,
      outcomes,
      statsAfter,
      errors: session.typingErrors,
      isControlled: false,
    }),
  };
}
